import { createQuery, Middleware, QueryHook } from 'react-query-kit';
import { get } from 'lodash';
import objectHash from 'object-hash';

import { STALE_TIME } from '@blockworks/platform/api';

import { VisualizationError, VisualizationErrorType } from '../models';
import { getVisualizationProps } from '../workflows';
import { validateExecution } from '../workflows/utils/validate-execution';

import { useWorker } from './visualization-worker.context';

const errorBoundary = (propsFunction?: (args: any) => any | Promise<any>) => async (props: any) => {
    try {
        validateExecution(props.execution);
    } catch (error) {
        console.warn('Error validating execution', error);

        if (error instanceof Error) {
            throw new VisualizationError({
                type: VisualizationErrorType.Execution,
                queryId: props.execution.queryId,
                visualizationId: props.id,
                executionId: props.execution.executionId,
                message: error.message,
            });
        }
    }

    if (!propsFunction) {
        console.warn('Error: Worker unavailable');

        throw new VisualizationError({
            type: VisualizationErrorType.Worker,
            message: 'Worker unavailable',
        });
    }

    try {
        const resultProps = await propsFunction(props);
        return resultProps;
    } catch (error) {
        if (error instanceof Error) {
            console.warn('Error getting visualization props', error);

            throw new VisualizationError({
                type: VisualizationErrorType.Configuration,
                queryId: props.execution.queryId,
                visualizationId: props.id,
                executionId: props.execution.executionId,
                message: error.message,
            });
        }
    }

    return null;
};

const workerMiddleware: Middleware<QueryHook<any, any>> = useNextQuery => {
    return options => {
        const worker = useWorker();
        const { variables } = options;
        let nextVariables: Record<string, any> = {
            title: variables.title,
            id: variables.id,
            executionId: get(variables, 'execution.executionId'),
            queryId: get(variables, 'execution.queryId'),
            params: get(variables, 'params'),
        };

        if (variables.editing) {
            nextVariables = {
                ...nextVariables,
                editing: variables.editing,
                config: objectHash(options.variables.config),
            };
        }

        return useNextQuery({
            ...options,
            staleTime: STALE_TIME.$5Minutes,
            variables: nextVariables,
            fetcher: async _ => {
                if (!worker) {
                    return errorBoundary(getVisualizationProps)(options.variables);
                }

                return errorBoundary(worker?.getVisualizationProps)(options.variables);
            },
        });
    };
};

const useWorkerVisualizationProps = createQuery({
    queryKey: ['get-visualization-props'],
    fetcher: errorBoundary(getVisualizationProps),
    use: [workerMiddleware],
});

export { useWorkerVisualizationProps };
